import { useEffect, useRef } from "react";
import {
  useCentraContext,
  useCentraReducerDispatch,
  useCentraSelectionResponse,
} from "@made-people/grease-centra";
import { useCartState } from "@made-people/grease-cart";
import { useCountriesAndLanguages } from "@made-people/grease-countries-and-languages";
import { CentraShippingMethod } from "@made-people/centra-checkout-api";
import { isAllCartItemsOMG } from "../utils/is-all-cart-items-in-omg";
import { useAllocationRule } from "../services/centra/allocation-rule/mm-allocation-rule-provider";

export const useAutoSwitchFromExpressShipping = () => {
  const selectionResponse = useCentraSelectionResponse();
  const { cart } = useCartState();
  const { checkoutAPIClient } = useCentraContext();
  const dispatch = useCentraReducerDispatch();
  const switchingRef = useRef(false);
  const { currentCountryCode } = useCountriesAndLanguages();
  const { allocationRule, hasWarehouse } = useAllocationRule();

  const hasLitWarehouse = hasWarehouse("LIT");
  const allInOMG = isAllCartItemsOMG(cart?.items ?? [], currentCountryCode, {
    name: allocationRule?.name ?? null,
    hasLitWarehouse,
  });
  const shippingMethods = selectionResponse?.shippingMethods ?? [];
  const selectedMethod = selectionResponse?.selection?.shippingMethod;
  const expressIsSelected = selectedMethod === "EXPRESS";
  const expressAvailable = hasLitWarehouse && allInOMG;
  const shouldSwitch = !expressAvailable && expressIsSelected;

  const fallbackMethod = shippingMethods.find(
    (m) => m.shippingMethod !== "EXPRESS",
  )?.shippingMethod;

  useEffect(() => {
    if (!shouldSwitch || !fallbackMethod || switchingRef.current) {
      return;
    }

    switchingRef.current = true;
    checkoutAPIClient
      .setShippingMethod({ shippingMethod: fallbackMethod })
      .then(({ success }) => {
        if (success) {
          dispatch({
            type: "SET_SELECTION_RESPONSE",
            payload: { selectionResponse: success },
          });
        }
      })
      .finally(() => {
        switchingRef.current = false;
      });
  }, [cart?.items]);
};

export const useExpressShippingFilter = (): CentraShippingMethod[] => {
  const selectionResponse = useCentraSelectionResponse();
  const { cart } = useCartState();
  const { currentCountryCode } = useCountriesAndLanguages();
  const { allocationRule, hasWarehouse } = useAllocationRule();
  const hasLitWarehouse = hasWarehouse("LIT");
  const allInOMG = isAllCartItemsOMG(cart?.items ?? [], currentCountryCode, {
    name: allocationRule?.name ?? null,
    hasLitWarehouse,
  });
  const expressAvailable = hasLitWarehouse && allInOMG;

  return (selectionResponse?.shippingMethods ?? []).filter(
    (method) => expressAvailable || method.shippingMethod !== "EXPRESS",
  );
};
